import React from 'react'
import { observer } from 'mobx-react-lite'

import { useTheme } from '@mui/material/styles'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import { P, Text } from '@mxenabled/mxui'

import { useGlobalCopyStore, useSettingsStore } from '../../../common'
import { Device } from '../../../core'
import { Dialog } from '../../../common/components'
import { DialogType } from '../../constants/Settings'
import { buildDynamicCopy } from '../../../core/utils/Localization'

interface DeviceDialogProps {
  dialogType: string
  isOpen: boolean
  onClose: () => void
  device?: Device
  deviceToken?: string
}

const DeviceDialog: React.FC<DeviceDialogProps> = ({
  dialogType,
  isOpen,
  onClose,
  device,
  deviceToken = '',
}) => {
  const theme = useTheme()
  const { common: commonCopy, settings: settingsCopy } = useGlobalCopyStore()
  const { updateDevice, deleteDevice, setAlert, loadDevices } = useSettingsStore()

  const [deviceName, setDeviceName] = React.useState('')

  React.useEffect(() => {
    if (device) setDeviceName(device.name)
  }, [device, dialogType])

  const getDialogTitle = () => {
    switch (dialogType) {
      case DialogType.Edit:
        return settingsCopy.mobile_devices_edit_label
      case DialogType.Delete:
        return settingsCopy.mobile_devices_delete_label
      default:
        return ''
    }
  }

  const getDialogPrimaryText = () => {
    switch (dialogType) {
      case DialogType.Token:
        return commonCopy.done_button
      case DialogType.Edit:
        return commonCopy.save_button
      case DialogType.Delete:
        return commonCopy.delete_button
      default:
        return ''
    }
  }

  const getDialogStyles = () => {
    const baseStyles = {
      '& .MuiDialogContent-root': {
        width: 400,
        pt: 0,
      },
    }

    if (dialogType === DialogType.Token) {
      return {
        ...baseStyles,
        '& .MuiToolbar-root:last-child': {
          padding: '32px 60px 48px',
          justifyContent: 'center',
        },
        '& .MuiButton-root:first-of-type': {
          display: 'none',
        },
        '& .MuiButton-root:last-child': {
          flex: 1,
        },
      }
    }

    return {
      ...baseStyles,
      '& .MuiToolbar-root:last-child': {
        gap: 8,
        padding: 24,
        justifyContent: 'flex-end',
      },
      ...(dialogType === DialogType.Delete && {
        '& .MuiTypography-body1': {
          padding: 0,
        },
      }),
    }
  }

  const handlePrimaryAction = async () => {
    if (dialogType === DialogType.Token) {
      onClose()
    }

    if (dialogType === DialogType.Edit && device) {
      const updatedDevice = { ...device, name: deviceName }
      const res = await updateDevice(updatedDevice)

      if (res.success) {
        setAlert(settingsCopy.mobile_devices_edit_success, 'success')
        loadDevices()
      } else {
        setAlert(
          buildDynamicCopy(settingsCopy.mobile_devices_edit_error, device.name) as string,
          'error',
        )
      }
      onClose()
    }

    if (dialogType === DialogType.Delete && device) {
      const res = await deleteDevice(device.guid)

      if (res.success) {
        setAlert(
          buildDynamicCopy(settingsCopy.mobile_devices_delete_success, device.name) as string,
          'success',
        )
        loadDevices()
      } else {
        setAlert(
          buildDynamicCopy(settingsCopy.mobile_devices_delete_error, device.name) as string,
          'error',
        )
      }
      onClose()
    }
  }

  return (
    <Dialog
      copy={{ title: getDialogTitle() }}
      isOpen={isOpen}
      onClose={onClose}
      onPrimaryAction={handlePrimaryAction}
      onSecondaryAction={onClose}
      primaryColor={dialogType === DialogType.Delete ? 'error' : undefined}
      primaryText={getDialogPrimaryText()}
      secondaryText={dialogType === DialogType.Token ? commonCopy.back : commonCopy.cancel_button}
      sx={getDialogStyles()}
    >
      {dialogType === DialogType.Token && (
        <Stack spacing={16} sx={{ px: 40, textAlign: 'center', width: '100%' }}>
          <Text bold={true} truncate={false} variant="body1">
            {settingsCopy.mobile_devices_access_label}
          </Text>
          <Text truncate={false} variant="body2">
            {settingsCopy.mobile_devices_access_info_1}
          </Text>
          <P
            sx={{
              border: `1px solid ${theme.palette.neutral.light}`,
              borderRadius: '4px',
              p: '16px 32px',
            }}
            variant="h1"
          >
            {deviceToken}
          </P>
          <Text truncate={false} variant="body2">
            {settingsCopy.mobile_devices_access_info_2}
          </Text>
        </Stack>
      )}
      {dialogType === DialogType.Edit && (
        <Stack>
          <TextField onChange={(e) => setDeviceName(e.target.value)} value={deviceName} />
        </Stack>
      )}
      {dialogType === DialogType.Delete && <P>{settingsCopy.mobile_devices_delete_info}</P>}
    </Dialog>
  )
}

export default observer(DeviceDialog)
